import { cn } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import { BETA_TIME_SERIES_METRICS } from './beta-time-series-utils';
import { BETA_METRIC_VARIANT_STYLES, type BetaMetricVariant } from './beta-metric-styles';

const BAR_WIDTHS = [100, 88, 76, 64, 54, 46, 38, 32, 26, 22];
const FIELD_TOGGLE_WIDTHS = [64, 56, 44, 48, 40];

function ColoredCardShimmer({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-white/25', className)}
      aria-hidden
    />
  );
}

export function BetaMetricCardSkeleton({
  variant,
  className,
}: {
  variant: BetaMetricVariant;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl p-5 shadow-[0_4px_20px_rgba(13,15,26,0.10)]',
        BETA_METRIC_VARIANT_STYLES[variant],
        className,
      )}
      aria-busy
      aria-label="Loading metric"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-6 -top-6 h-[90px] w-[90px] rounded-full bg-white/10"
      />
      <div className="mb-2 flex items-start justify-between">
        <ColoredCardShimmer className="h-3 w-16" />
        <ColoredCardShimmer className="h-6 w-14 rounded-full" />
      </div>
      <ColoredCardShimmer className="mb-2 h-8 w-28" />
      <ColoredCardShimmer className="h-9 w-full" />
    </div>
  );
}

export function BetaMetricCardRowSkeleton() {
  const variants: BetaMetricVariant[] = ['clicks', 'conversions', 'impressions', 'spend'];
  return (
    <>
      {variants.map((variant) => (
        <BetaMetricCardSkeleton key={variant} variant={variant} />
      ))}
    </>
  );
}

export function BetaTimeSeriesMetricChipsSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-2" aria-hidden>
      {BETA_TIME_SERIES_METRICS.map((metric) => (
        <div
          key={metric.key}
          className="h-[30px] w-[92px] animate-pulse rounded-full opacity-80"
          style={{ backgroundColor: metric.colour }}
        />
      ))}
    </div>
  );
}

export function BetaChartAreaSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('relative w-full overflow-hidden rounded-lg bg-[#FAFAFB]', className)}
      aria-hidden
    >
      <div className="absolute inset-x-0 top-0 flex h-full flex-col justify-between px-2 py-4">
        {[0, 1, 2, 3, 4].map((line) => (
          <div key={line} className="h-px w-full bg-[#EDEEF2]" />
        ))}
      </div>
      <div className="absolute inset-x-6 bottom-6 top-6">
        <svg
          className="h-full w-full animate-pulse text-[#D9DBE3]"
          viewBox="0 0 400 120"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            d="M0,95 C40,88 80,72 120,68 C160,64 200,78 240,58 C280,38 320,42 360,28 C380,22 390,18 400,12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="M0,95 C40,88 80,72 120,68 C160,64 200,78 240,58 C280,38 320,42 360,28 C380,22 390,18 400,12 L400,120 L0,120 Z"
            fill="currentColor"
            fillOpacity="0.12"
          />
        </svg>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex justify-between px-4 pb-2">
        {[0, 1, 2, 3, 4, 5].map((tick) => (
          <Skeleton key={tick} className="h-2.5 w-8" />
        ))}
      </div>
    </div>
  );
}

export function BetaBreakdownBarsSkeleton({ colour = '#2563EB' }: { colour?: string }) {
  return (
    <div className="space-y-3 py-1" aria-hidden>
      {BAR_WIDTHS.map((width, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-3 w-[88px] shrink-0" />
          <div className="flex min-w-0 flex-1 items-center">
            <div
              className="h-5 animate-pulse rounded-r"
              style={{ width: `${width}%`, backgroundColor: colour, opacity: 0.35 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function BetaBreakdownTableSkeleton({ colour = '#2563EB' }: { colour?: string }) {
  return (
    <div className="space-y-0" aria-hidden>
      {BAR_WIDTHS.map((width, index) => (
        <div
          key={index}
          className="flex items-center gap-3 border-b border-[#EDEEF2] py-2.5 last:border-b-0"
        >
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full opacity-60"
                style={{ backgroundColor: colour }}
              />
              <Skeleton className="h-3.5 w-[72%] max-w-[180px]" />
            </div>
            <div className="h-1 rounded-sm bg-[#EDEEF2]">
              <div
                className="h-1 animate-pulse rounded-sm"
                style={{ width: `${width}%`, backgroundColor: colour, opacity: 0.35 }}
              />
            </div>
          </div>
          <Skeleton className="h-3.5 w-12 shrink-0" />
          <Skeleton className="h-3 w-14 shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function BetaFieldToggleSkeleton() {
  return (
    <div className="flex flex-wrap gap-2" aria-hidden>
      {FIELD_TOGGLE_WIDTHS.map((width, index) => (
        <Skeleton key={index} className="h-[30px] rounded-md" style={{ width }} />
      ))}
    </div>
  );
}

export function BetaFilterSelectSkeleton() {
  return <Skeleton className="h-[38px] w-full rounded-lg" aria-hidden />;
}
